import navbar from '../navigation/vertical/index'

// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = obj => Object.keys(obj).length === 0

// ** Returns K format from a number
export const kFormatter = num => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num)

// ** Converts HTML to string
export const htmlToString = html => html.replace(/<\/?[^>]+(>|$)/g, '')

// ** Checks if the passed date is today
const isToday = date => {
  const today = new Date()
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  )
}

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (value, formatting = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!value) return value
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value)
  let formatting = { month: 'short', day: 'numeric' }

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' }
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value))
}

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData') || localStorage.getItem(`${process.env.REACT_APP_USERDATA}`)
export const getUserData = () => JSON.parse(localStorage.getItem('userData') || localStorage.getItem(`${process.env.REACT_APP_USERDATA}`) || 'null')
export const allowedImages = () => ['png', 'jpeg', 'jpg']
export const allowedFiles = () => ['docx', 'pdf', 'doc', 'xls', 'xlsx', 'png', 'jpeg', 'jpg']
export const allowedFilesErrorMessages = () => {
  return {
    IMAGE_ONLY: 'The file must be type of (.jpeg, .png, .jpg)',
    IMAGE_AND_FILES: 'The file must be type of (.jpeg, .png, .jpg, .pdf, .doc, .docx, .xls, .xlsx)'
  }
}
/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */

const subAdminHomeRoute = () => {
  const permissions = getUserData()?.permissions
  const navItem = navbar.find(item => item.id === permissions?.[0])
  if (navItem) {
    const child = navItem?.children?.[0]
    return child?.navLink ?? navItem?.navLink
  }
  return null
}

export const getHomeRouteForLoggedInUser = userRole => {
  if (userRole === 'admin') return '/dashboard/admin'
  if (userRole === 'sub_admin') return subAdminHomeRoute()
  if (userRole === 'rd') return '/dashboard/rd'
  if (userRole === 'brand') return '/dashboard/brands'
  return '/login'
}

// ** React Select Theme Colors
export const selectThemeColors = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed' // for input hover border-color
  }
})

/**
 * Returns '#ffffff' or '#1e293b' based on perceived brightness/contrast of hex color
 * Supports 3-digit, 6-digit hex, with or without '#', and handles fallbacks gracefully.
 * @param {String} hexColor
 * @param {String} lightColor default '#ffffff'
 * @param {String} darkColor default '#1e293b'
 * @returns {String}
 */
export const getContrastTextColor = (hexColor, lightColor = '#ffffff', darkColor = '#1e293b') => {
  if (!hexColor || typeof hexColor !== 'string') return lightColor
  let color = hexColor.replace('#', '').trim()
  if (color.length === 3) {
    color = color.split('').map(c => c + c).join('')
  }
  if (color.length !== 6) return lightColor

  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)

  if (isNaN(r) || isNaN(g) || isNaN(b)) return lightColor

  // YIQ luminance formula: threshold >= 190 ensures vibrant brand colors like #56B6FF retain white text, while bright pastels/whites use dark text
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 190 ? darkColor : lightColor
}

/**
 * Returns whether a color is light
 * @param {String} hexColor
 * @returns {Boolean}
 */
export const isLightColor = (hexColor) => {
  if (!hexColor || typeof hexColor !== 'string') return false
  let color = hexColor.replace('#', '').trim()
  if (color.length === 3) {
    color = color.split('').map(c => c + c).join('')
  }
  if (color.length !== 6) return false
  const r = parseInt(color.substring(0, 2), 16)
  const g = parseInt(color.substring(2, 4), 16)
  const b = parseInt(color.substring(4, 6), 16)
  if (isNaN(r) || isNaN(g) || isNaN(b)) return false
  return ((r * 299 + g * 587 + b * 114) / 1000) >= 190
}

